/**
 * art.ts — the meshes players are made of.
 *
 * The BARE figure and the bare weapon: what every player is under whatever they
 * are wearing. Kit — the sixteen pieces of the armour catalogue — lives next
 * door in `kit.ts`, and the posing that drives both lives in `figure.ts`.
 *
 * The body is built as jointed SEGMENTS: trunk, two-bone arms, two-bone legs
 * with a knee, and empties at the head, chest, pelvis, hands and feet. Those
 * empties are the mounting points kit is worn on, which is what lets a piece of
 * plate move with the part of the body it covers rather than being pinned to a
 * fixed spot on a group that never turns.
 *
 * Everything here obeys one rule: the drawn shapes must stay inside the shapes
 * the server actually tests against. The body art fits within the body capsule
 * and the blade is exactly SWORD_LENGTH from hilt to tip, because a picture that
 * disagrees with the hitbox is a bug, not a style.
 */

import * as THREE from 'three';
import { FEEL } from '@dueler/shared';
import {
  createClothMaterial,
  createFleshMaterial,
  createLeatherMaterial,
  createSteelMaterial,
  denseLimbGeometry,
  markNearDetail,
} from './surfaces.js';

export interface Built {
  object: THREE.Object3D;
  /** Everything created here, for renderSync to dispose on removal. */
  disposables: { dispose(): void }[];
}

export interface BuiltSword extends Built {
  /**
   * The blade's own frame, rolled so a crossguard added to it stands vertically.
   * Armour hangs the sword's furniture here, which is why it is exposed: the
   * parts have to share the blade's roll or the guard comes out sideways.
   */
  furniture: THREE.Group;
  /**
   * The plain pill every player carries, exposed so a set that brings its OWN
   * blade can hide it.
   *
   * A named sword is a shape, not a paint job — Chrysamere is a broad leaf, and
   * you cannot get a broad leaf by decorating a round bar: the bar bulges out
   * through the flats of anything flatter than itself. So a set with its own
   * blade replaces this one rather than dressing it, under the same rule the
   * pill obeys — spans exactly SWORD_LENGTH, stays inside SWORD_RADIUS. Swap the
   * art, never the capsule.
   */
  bareBlade: THREE.Mesh;
}

/**
 * A sword, built along +Y with the GUARD LINE AT THE ORIGIN: blade from 0 up to
 * SWORD_LENGTH, grip hanging below 0 where the hands are.
 *
 * Deliberately GENERIC — a tapered bar of steel and a grip, nothing more. This
 * is the shape the server actually sweeps: a capsule from hilt to tip. Detailing
 * it here would be drawing a crossguard and a pommel that no collider knows
 * about, on a weapon every player carries identically. The furniture belongs to
 * the armour you are wearing (see `buildSwordFurniture`), so kitting out is what
 * turns the bar into a sword.
 *
 * That origin is not arbitrary: the hilt point is the start of the swept capsule
 * and the arms are solved onto the grip below it. The blade spans the FULL
 * SWORD_LENGTH, so what is drawn is precisely the capsule the server sweeps.
 */
export function buildSword(): BuiltSword {
  const group = new THREE.Group();
  // Rolled a quarter turn about the blade axis so anything flat added to it —
  // a crossguard, above all — stands vertically. Purely cosmetic: a capsule
  // hitbox is unchanged by a roll about its own axis.
  const furniture = new THREE.Group();
  furniture.rotation.y = Math.PI / 2;
  group.add(furniture);
  const disposables: { dispose(): void }[] = [];

  const steel = createSteelMaterial(0xb9c2cf, { heavy: true });
  const grey = createLeatherMaterial(0x4a4a52);
  disposables.push(steel, grey);

  // Dense tapered blade spanning the exact hitbox length.
  const bladeLength = FEEL.SWORD_LENGTH;
  const blade = new THREE.CapsuleGeometry(
    FEEL.SWORD_RADIUS * 0.62,
    bladeLength - FEEL.SWORD_RADIUS * 1.24,
    12,
    28,
  );
  taperAlongY(blade, 0.55);
  const bladeMesh = new THREE.Mesh(blade, steel);
  bladeMesh.position.y = bladeLength / 2;
  bladeMesh.castShadow = true;
  furniture.add(bladeMesh);
  disposables.push(blade);

  // A bare grip. Armour wraps and caps it.
  const gripLength = FEEL.SWORD_GRIP_LENGTH;
  const grip = new THREE.CapsuleGeometry(0.028, gripLength * 0.7, 8, 16);
  const gripMesh = new THREE.Mesh(grip, grey);
  gripMesh.position.y = -gripLength / 2;
  gripMesh.castShadow = true;
  furniture.add(gripMesh);
  disposables.push(grip);

  return { object: group, disposables, furniture, bareBlade: bladeMesh };
}

export interface BuiltBody extends Built {
  /**
   * The parts the renderer poses each frame. Every one of them is drawn as a
   * SEGMENT between two points, so it is connected at both ends by construction:
   * legs run foot-to-knee-to-hip, the torso runs hip-to-shoulder, the arms run
   * shoulder-to-elbow-to-hand. Nothing is a rigid block that can tear away from
   * what it is attached to, which is what happened when the shoulders started
   * moving with the swing and the trunk did not.
   */
  /** The team-coloured surcoat on the bare trunk. Replaced by any chest piece. */
  tabard: THREE.Mesh;
  /** Chest: shoulders down to the ribs. */
  torso: THREE.Mesh;
  /** Stomach: ribs down to the hips. Narrower, so the trunk tapers. */
  stomach: THREE.Mesh;
  hips: THREE.Mesh;
  /** Neck, head and brow — carried on the shoulder line and oriented with it. */
  head: THREE.Group;
  /**
   * An empty at the shoulder line, turned with the chest. Kit worn on the trunk
   * is parented here and needs no per-frame work of its own: a cuirass authored
   * in this space twists, leans, lunges and bows because the anchor does.
   *
   * This is the same transform the shoulders are derived from, so a pauldron
   * hung at a fixed local offset lands exactly on the shoulder point the arm is
   * solved from and cannot come adrift from the arm hanging out of it.
   */
  chest: THREE.Group;
  /** An empty at the hips. Faulds and skirts hang here; hips do not twist. */
  pelvis: THREE.Group;
  /** Thighs, hip to knee. Index 0 is the character's left. */
  thighs: [THREE.Mesh, THREE.Mesh];
  /** Shins, knee to ankle. */
  shins: [THREE.Mesh, THREE.Mesh];
  /** Knee joints, on the solved knee points. */
  knees: [THREE.Mesh, THREE.Mesh];
  /** Feet, as empties at the ankle pointing down the toes. Boots ride these. */
  feet: [THREE.Group, THREE.Group];
  /**
   * The FLESH inside the head and foot mounts, listed separately from the
   * mounts themselves.
   *
   * The distinction is not fussiness — it is a bug that has already happened. A
   * great helm is parented to `head`, so hiding `head` to stop the skull poking
   * through the visor hid the helm along with it. A mount must stay visible
   * while what it carries is taken away, which means the two can never be the
   * same object.
   */
  headFlesh: THREE.Object3D[];
  footFlesh: THREE.Object3D[];
  /** Deltoids, placed on the same shoulder points the arms are solved from. */
  shoulders: [THREE.Mesh, THREE.Mesh];
  /** Elbows, on the solved elbow points. Without them the arm has a kink in it. */
  elbows: [THREE.Mesh, THREE.Mesh];
}

/** Thigh bone length, m. Hip to knee. */
export const THIGH_BONE = 0.45;
/** Shin bone length, m. Knee to ankle. */
export const SHIN_BONE = 0.42;
/** Ankle height above the sole, m. The foot hangs below this. */
export const ANKLE_HEIGHT = 0.075;
/** Half the distance between the feet, m. */
export const STANCE_HALF_WIDTH = 0.095;

/** Nominal length the stretchable limb capsules are built at. */
export const LIMB_UNIT = 0.4;

/**
 * How far inside the hurtbox the FLESH is drawn, m.
 *
 * The capsule is the hitbox and the body is a thing worn inside it, with this
 * much room left over for armour. Kit is fitted into that gap, so a plate can
 * be bigger than the limb it covers and still be a shape the server would
 * actually hit — which is the ordering that stops "make the pauldron fit" and
 * "make the pauldron cover the shoulder" from being the same argument.
 */
export const BODY_INSET = 0.06;

/**
 * Clearance a piece of kit needs over the flesh it covers, m.
 */
export const KIT_CLEARANCE = 0.012;

/**
 * The deltoid: FLESH, sized to the arm it caps.
 *
 * This used to be derived backwards — from how wide a pauldron was allowed to
 * be inside the old single body capsule, which left a shoulder ball far too
 * big and a pauldron far too small. The capsule stopped being a hurtbox when
 * the limbs became one, so the constraint it imposed is gone: a shoulder is
 * now sized like the joint it is, and kit is sized to cover it.
 */
const DELTOID_R = FEEL.ARM_RADIUS + 0.02;

/**
 * Widest a shoulder piece may be, m.
 *
 * Bounded by the limb rig rather than by the body capsule: kit has to stay
 * wrapped around the limb it is worn on (`kit.test.ts`), and a shoulder piece
 * sits on the joint between the chest and upper-arm capsules, so it has real
 * room. Enough for a gothic pauldron that overhangs the arm, which is the
 * whole silhouette of one.
 */
export const PAULDRON_R = DELTOID_R + 0.055;

/**
 * The skull's dimensions, in HEAD-MOUNT space.
 *
 * Exported because every helmet has to be sized against them. A helm built to
 * hand-picked numbers stops fitting the moment the head moves — which is
 * exactly what happened when the head was raised and the mail coif was left
 * behind, so the crown poked out through the top of it.
 *
 * `centreY` is solved from two things it must satisfy at once: the skull's
 * underside must clear the shoulders, or the head sits inside the chest and
 * every breastplate swallows it; and its crown must stay under BODY_HEIGHT, or
 * the figure is taller than the capsule it walks in.
 */
const SKULL_BASE_R = 0.118;
/** How far the skull's underside clears the shoulder line, m. */
const NECK_CLEARANCE = 0.035;
export const SKULL = {
  rx: SKULL_BASE_R,
  ry: SKULL_BASE_R * 1.1,
  rz: SKULL_BASE_R * 1.06,
  centreY: Math.min(
    FEEL.BODY_HEIGHT - FEEL.SLASH_PIVOT[1] - SKULL_BASE_R * 1.1,
    NECK_CLEARANCE + SKULL_BASE_R * 1.1,
  ),
} as const;
/** The very top of the skull, in head-mount space. Helms must clear it. */
export const SKULL_TOP = SKULL.centreY + SKULL.ry;

/** Thigh and shin radii, m. Kit worn over them is built a plate's thickness up. */
export const THIGH_R = 0.082;
export const SHIN_R = 0.062;

/**
 * A figure, built about the capsule centre. Deliberately slimmer than
 * BODY_RADIUS: the capsule is the hurtbox, and art poking outside it would
 * promise hits that never land.
 */
export function buildBody(isSelf: boolean): BuiltBody {
  const group = new THREE.Group();
  const disposables: { dispose(): void }[] = [];

  // The bare figure is a MAN, not a team colour. Team identity lives on garments.
  const cloth = createClothMaterial(0x4a4438);
  const skin = createFleshMaterial();
  const dark = createClothMaterial(0x24282f);
  const team = createClothMaterial(isSelf ? 0x3f6fb5 : 0xa8483a);
  disposables.push(cloth, skin, dark, team);

  const h = FEEL.BODY_HEIGHT;
  const shade = (mesh: THREE.Mesh): THREE.Mesh => {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  };

  // Dense anatomical limbs — still CapsuleGeometry at LIMB_UNIT so pose stretch works.
  const CHEST_R = 0.148;
  const chestGeo = denseLimbGeometry(CHEST_R, LIMB_UNIT, {
    radial: 16,
    tipKeep: 1.3,
    wide: 1.14,
    deep: 0.82,
  });
  const torso = shade(new THREE.Mesh(chestGeo, cloth));
  torso.userData['radius'] = CHEST_R;
  group.add(torso);
  disposables.push(chestGeo);

  const WAIST_R = 0.126;
  const stomachGeo = denseLimbGeometry(WAIST_R, LIMB_UNIT, {
    radial: 14,
    tipKeep: 1.12,
    wide: 1.1,
    deep: 0.84,
  });
  const stomach = shade(new THREE.Mesh(stomachGeo, cloth));
  stomach.userData['radius'] = WAIST_R;
  group.add(stomach);
  disposables.push(stomachGeo);

  const hipsGeo = denseLimbGeometry(0.13, 0.1, { radial: 14, wide: 1.28, deep: 0.88 });
  const hips = shade(new THREE.Mesh(hipsGeo, dark));
  group.add(hips);
  disposables.push(hipsGeo);

  // Separate geos per limb so later LOD/dispose is safe (shared geo was fine
  // before; denser meshes still share within a side pair).
  const thighGeoL = denseLimbGeometry(THIGH_R, LIMB_UNIT, { radial: 12, tipKeep: 0.92, deep: 0.94 });
  const thighGeoR = denseLimbGeometry(THIGH_R, LIMB_UNIT, { radial: 12, tipKeep: 0.92, deep: 0.94 });
  const shinGeoL = denseLimbGeometry(SHIN_R, LIMB_UNIT, { radial: 12, tipKeep: 0.88, deep: 0.94 });
  const shinGeoR = denseLimbGeometry(SHIN_R, LIMB_UNIT, { radial: 12, tipKeep: 0.88, deep: 0.94 });
  disposables.push(thighGeoL, thighGeoR, shinGeoL, shinGeoR);
  const limb = (geo: THREE.BufferGeometry, radius: number): THREE.Mesh => {
    const mesh = shade(new THREE.Mesh(geo, dark));
    mesh.userData['radius'] = radius;
    group.add(mesh);
    return mesh;
  };
  const thighs: [THREE.Mesh, THREE.Mesh] = [limb(thighGeoL, THIGH_R), limb(thighGeoR, THIGH_R)];
  const shins: [THREE.Mesh, THREE.Mesh] = [limb(shinGeoL, SHIN_R), limb(shinGeoR, SHIN_R)];

  const kneeGeo = new THREE.SphereGeometry(0.072, 14, 12);
  kneeGeo.scale(1, 1, 0.95);
  disposables.push(kneeGeo);
  const knees: [THREE.Mesh, THREE.Mesh] = [
    shade(new THREE.Mesh(kneeGeo, dark)),
    shade(new THREE.Mesh(kneeGeo, dark)),
  ];
  group.add(knees[0], knees[1]);

  const shoeGeo = new THREE.CapsuleGeometry(0.052, 0.11, 6, 12);
  shoeGeo.rotateX(Math.PI / 2);
  shoeGeo.scale(1.05, 0.72, 1);
  disposables.push(shoeGeo);
  const footFlesh: THREE.Object3D[] = [];
  const foot = (): THREE.Group => {
    const anchor = new THREE.Group();
    const shoe = shade(new THREE.Mesh(shoeGeo, dark));
    shoe.position.set(0, -ANKLE_HEIGHT + 0.038, -0.028);
    anchor.add(shoe);
    footFlesh.push(shoe);
    group.add(anchor);
    return anchor;
  };
  const feet: [THREE.Group, THREE.Group] = [foot(), foot()];

  const shoulderGeo = new THREE.SphereGeometry(DELTOID_R, 16, 12);
  shoulderGeo.scale(1, 1, 0.92);
  disposables.push(shoulderGeo);
  const shoulders: [THREE.Mesh, THREE.Mesh] = [
    shade(new THREE.Mesh(shoulderGeo, skin)),
    shade(new THREE.Mesh(shoulderGeo, skin)),
  ];
  group.add(shoulders[0], shoulders[1]);

  const elbowGeo = new THREE.SphereGeometry(0.075, 14, 12);
  elbowGeo.scale(1, 1, 0.95);
  disposables.push(elbowGeo);
  const elbows: [THREE.Mesh, THREE.Mesh] = [
    shade(new THREE.Mesh(elbowGeo, skin)),
    shade(new THREE.Mesh(elbowGeo, skin)),
  ];
  group.add(elbows[0], elbows[1]);

  // Soft-draped tabard (subdivided plane wrapped as a thin shell).
  const tabardGeo = new THREE.BoxGeometry(0.2, 0.42, 0.02, 4, 12, 2);
  const tabard = shade(new THREE.Mesh(tabardGeo, team));
  tabard.position.set(0, h * 0.66 - h / 2, -0.13);
  group.add(tabard);
  disposables.push(tabardGeo);

  const chest = new THREE.Group();
  const pelvis = new THREE.Group();
  group.add(chest, pelvis);

  const head = new THREE.Group();
  head.name = 'head';
  group.add(head);

  const neckGeo = new THREE.CylinderGeometry(0.056, 0.084, 0.15, 28, 4);
  const neck = shade(new THREE.Mesh(neckGeo, skin));
  neck.position.y = 0.072;
  head.add(neck);
  disposables.push(neckGeo);

  const crownY = SKULL.centreY;
  const skullGeo = new THREE.SphereGeometry(SKULL.rx, 20, 16);
  skullGeo.scale(1, SKULL.ry / SKULL.rx, 1.06);
  const skull = shade(new THREE.Mesh(skullGeo, skin));
  skull.position.y = crownY;
  head.add(skull);
  disposables.push(skullGeo);

  // Jaw / chin block — readable facing cue.
  const jawGeo = new THREE.SphereGeometry(0.082, 16, 12);
  jawGeo.scale(1, 0.78, 1.12);
  const jaw = shade(new THREE.Mesh(jawGeo, skin));
  jaw.position.set(0, crownY - 0.058, -0.038);
  head.add(jaw);
  disposables.push(jawGeo);

  // Nose bridge + brow ridge (near-detail; LOD can hide).
  const browGeo = new THREE.BoxGeometry(0.16, 0.028, 0.06, 2, 2, 2);
  const brow = shade(new THREE.Mesh(browGeo, skin));
  brow.position.set(0, crownY + 0.022, -0.09);
  markNearDetail(brow);
  head.add(brow);
  disposables.push(browGeo);

  const noseGeo = new THREE.CapsuleGeometry(0.014, 0.028, 6, 12);
  const nose = shade(new THREE.Mesh(noseGeo, skin));
  nose.rotation.x = Math.PI / 2;
  nose.position.set(0, crownY - 0.01, -0.108);
  markNearDetail(nose);
  head.add(nose);
  disposables.push(noseGeo);

  // Eye sockets — dark recesses so the face reads at duel distance.
  const socketGeo = new THREE.SphereGeometry(0.018, 12, 10);
  disposables.push(socketGeo);
  const socketMat = new THREE.MeshStandardMaterial({ color: 0x1a1410, roughness: 0.95 });
  disposables.push(socketMat);
  const sockets: THREE.Mesh[] = [];
  for (const side of [-1, 1] as const) {
    const socket = shade(new THREE.Mesh(socketGeo, socketMat));
    socket.scale.set(1.15, 0.75, 0.55);
    socket.position.set(side * 0.038, crownY + 0.008, -0.1);
    markNearDetail(socket);
    head.add(socket);
    sockets.push(socket);
  }

  // Hair / scalp cap — dark cloth, near detail.
  const scalpGeo = new THREE.SphereGeometry(SKULL.rx * 1.02, 14, 10, 0, Math.PI * 2, 0, Math.PI * 0.55);
  const scalp = shade(new THREE.Mesh(scalpGeo, dark));
  scalp.position.y = crownY + 0.01;
  markNearDetail(scalp);
  head.add(scalp);
  disposables.push(scalpGeo);

  const headFlesh: THREE.Object3D[] = [neck, skull, jaw, brow, nose, scalp, ...sockets];

  return {
    object: group,
    disposables,
    headFlesh,
    footFlesh,
    tabard,
    torso,
    stomach,
    hips,
    head,
    chest,
    pelvis,
    thighs,
    shins,
    knees,
    feet,
    shoulders,
    elbows,
  };
}

/** A fingered mitt. Cosmetic only — stays inside the hand mount. */
export function buildHand(_isSelf: boolean): Built {
  const disposables: { dispose(): void }[] = [];
  const skin = createFleshMaterial();
  disposables.push(skin);
  const root = new THREE.Group();

  const palmGeo = new THREE.SphereGeometry(0.048, 12, 10);
  const palm = new THREE.Mesh(palmGeo, skin);
  palm.scale.set(1.05, 1.2, 0.85);
  palm.castShadow = true;
  root.add(palm);
  disposables.push(palmGeo);

  // Four fingers + thumb as short capsules (near-detail; no shadow casters).
  const fingerGeo = new THREE.CapsuleGeometry(0.009, 0.028, 3, 6);
  disposables.push(fingerGeo);
  const tips = [-0.028, -0.01, 0.01, 0.028];
  for (let i = 0; i < tips.length; i += 1) {
    const finger = new THREE.Mesh(fingerGeo, skin);
    finger.position.set(tips[i]!, 0.042, -0.012);
    finger.rotation.x = -0.35;
    markNearDetail(finger);
    root.add(finger);
  }
  const thumbGeo = new THREE.CapsuleGeometry(0.011, 0.022, 3, 6);
  const thumb = new THREE.Mesh(thumbGeo, skin);
  thumb.position.set(0.038, 0.01, -0.01);
  thumb.rotation.set(-0.2, 0, -0.9);
  markNearDetail(thumb);
  root.add(thumb);
  disposables.push(thumbGeo);

  return { object: root, disposables };
}

/**
 * Narrows a box geometry towards its +Y end. `keep` is the fraction of the
 * original width left at the tip.
 */
export function taperAlongY(geometry: THREE.BufferGeometry, keep: number): void {
  const pos = geometry.attributes['position'] as THREE.BufferAttribute;
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const span = box.max.y - box.min.y || 1;

  for (let i = 0; i < pos.count; i += 1) {
    const y = pos.getY(i);
    const t = (y - box.min.y) / span;
    const scale = 1 - (1 - keep) * t;
    pos.setX(i, pos.getX(i) * scale);
    pos.setZ(i, pos.getZ(i) * scale);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
}

/**
 * Reshapes a box into a blade by driving its width and thickness from profiles
 * along its own length. `width` and `thick` are given the run 0..1 from the
 * bottom of the box to the top and return metres.
 *
 * Segmented boxes only, and along Y only: the profiles are sampled at whatever
 * rings the geometry already has, so a box built with one height segment comes
 * out a wedge no matter what curve you hand it.
 */
export function profileAlongY(
  geometry: THREE.BufferGeometry,
  width: (t: number) => number,
  thick: (t: number) => number,
): void {
  const pos = geometry.attributes['position'] as THREE.BufferAttribute;
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const span = box.max.y - box.min.y || 1;

  for (let i = 0; i < pos.count; i += 1) {
    const t = (pos.getY(i) - box.min.y) / span;
    // The unit box runs -0.5..0.5, so the sign carries the side and the profile
    // carries the size: halve it and the returned number is the full width.
    pos.setX(i, Math.sign(pos.getX(i)) * (width(t) / 2));
    pos.setZ(i, Math.sign(pos.getZ(i)) * (thick(t) / 2));
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
}

/** Hermite ease between two values, for profiles that have to meet smoothly. */
export const smooth = (a: number, b: number, t: number): number => {
  const k = t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t);
  return a + (b - a) * k;
};
